/**
 * Bank booking suggestions, booking rules and the missing-documents view (backend
 * modules/Accounting, S6b): suggestions are complete posting proposals that a person
 * confirms, changes or rejects; rules propose but never book; missing documents are
 * input-tax items without a document attached/assigned in exonn ERP yet.
 */
import type { OwnerResource } from "../../owner";
import type { AccountingAccountResource } from "../../accounting-account";
import type { BankTransactionResource } from "../../bank-transaction";
import type {
  AccountingPostingExternalDocumentResource,
  InvoiceDuplicateMatch,
} from "../../accounting-posting";

export enum BookingSuggestionStatusEnum {
  Open = "open",
  Confirmed = "confirmed",
  Changed = "changed",
  Rejected = "rejected",
}

export enum BookingRuleSourceEnum {
  Manual = "manual",
  Learned = "learned",
}

export enum BookingRuleRecurrenceEnum {
  None = "none",
  Monthly = "monthly",
  Quarterly = "quarterly",
  Yearly = "yearly",
  Irregular = "irregular",
}

export enum BookingScopeEnum {
  Full = "full",
  PaymentOnly = "payment_only",
}

export type BookingSuggestionPosition = {
  account_number: string | null;
  account_title: string | null;
  account_type: string | null;
  amount: number | string;
  tax_type: string | null;
};

export interface AccountingBookingRuleResource {
  id: string;
  owner_id: string;
  is_active: boolean;
  priority: number;
  title: string;
  match_iban: string | null;
  match_counterparty: string | null;
  match_text_pattern: string | null;
  amount_min: string | null;
  amount_max: string | null;
  amount_tolerance_percent: string | null;
  recurrence: BookingRuleRecurrenceEnum;
  account_type: string;
  vat_percent: string | null;
  tax_type: string | null;
  booking_scope: BookingScopeEnum;
  source: BookingRuleSourceEnum;
  confirmations_count: number;
  last_confirmed_at: string | null;
  owner?: OwnerResource | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface AccountingBookingSuggestionResource {
  id: string;
  owner_id: string;
  source_type: string;
  source_id: string;
  bank_transaction_id: string | null;
  suggested_account_type: string;
  suggested_account_id: string | null;
  suggested_account?: AccountingAccountResource | null;
  suggested_vat_percent: string | null;
  suggested_tax_type: string | null;
  booking_scope: BookingScopeEnum;
  confidence: string;
  reason: string;
  matched_rule_id: string | null;
  matched_rule?: AccountingBookingRuleResource | null;
  context: {
    positions?: BookingSuggestionPosition[];
    input_tax_notice?: string | null;
    matcher?: string;
    occurrences?: number;
    [key: string]: unknown;
  } | null;
  status: BookingSuggestionStatusEnum;
  confirmed_by: string | null;
  confirmed_by_name?: string | null;
  confirmed_at: string | null;
  resulting_posting_id: string | null;
  note: string | null;
  bank_transaction?: BankTransactionResource | null;
  owner?: OwnerResource | null;
  created_at: string | null;
  updated_at: string | null;
}

export type ConfirmBookingSuggestionRequestBody = {
  account_id?: string | null;
  vat_percent?: number | null;
  note?: string | null;
};

/** S1: Ausgangsrechnung oder Gutschrift ohne Buchung, mit dem zuletzt erfassten Grund. */
export interface AccountingUnpostedDocumentResource {
  id: string;
  owner_id: string | null;
  document_type: string;
  document_number: string | null;
  document_date: string | null;
  document_total_gross: string | number | null;
  /** Nullbeleg: bekommt systembedingt keine Buchung */
  zero_amount: boolean;
  /** Belegdatum im gesperrten Buchungszeitraum */
  period_blocked: boolean;
  /** false: Buchung fehlt; true: Buchung vorhanden, letzte Aktualisierung fehlgeschlagen (veraltet) */
  has_posting: boolean;
  /** Grund des letzten Fehlschlags erfasst */
  has_failure: boolean;
  posting_failure_operation: "create" | "update" | null;
  posting_failure_reason: string | null;
  posting_failed_at: string | null;
  /** Zahl der fehlgeschlagenen Versuche; null ohne erfassten Grund */
  posting_failure_attempts: number | null;
  owner?: OwnerResource | null;
  created_at: string | null;
}

export interface AccountingMissingDocumentResource {
  id: string;
  owner_id: string;
  accounting_posting_id: string | null;
  posting_number?: string | null;
  description?: string | null;
  external_document_number: string | null;
  supplier_id: string | null;
  tax_date: string | null;
  invoice_date: string | null;
  /** M2: `unconfirmed` = vorläufig im Monat der Bankbuchung, Zeitpunkt noch zu bestätigen */
  service_date_source?: string;
  tax_date_confirmed?: boolean;
  invoice_reference?: AccountingPostingExternalDocumentResource | null;
  vat_percent: string;
  net_amount: string;
  vat_amount: string;
  gross_amount: string;
  input_tax_status: string | null;
  age_days: number | null;
  warnings: string[];
  owner?: OwnerResource | null;
  created_at: string | null;
}

export type MissingDocumentComparisonField = {
  field: string;
  booked: unknown;
  document: unknown;
  difference: unknown;
  ok: boolean;
  /** match | mismatch | not_comparable | informational */
  status?: string;
};

export type MissingDocumentPlausibilityIssue = {
  code: string;
  message: string;
};

export type MissingDocumentAttachResult = {
  result: "verified" | "mismatch" | "inconsistent";
  plausibility: MissingDocumentPlausibilityIssue[];
  comparison: {
    match: boolean;
    fields: MissingDocumentComparisonField[];
    warnings: string[];
  };
  review_item_id: string | null;
  /** bei einer ersetzten Bankbuchung ohne bestätigten Zeitpunkt: der neue Vorgang */
  vat_entry_id?: string;
  vat_entry: AccountingMissingDocumentResource;
};

export type InvoiceReferenceSaveResult = {
  reference: AccountingPostingExternalDocumentResource;
  vat_entries: AccountingMissingDocumentResource[];
  /** true: der Vorgang wurde mit den Rechnungsdaten neu erfasst (neue Kennung) */
  rewritten: boolean;
  duplicates: InvoiceDuplicateMatch[];
  notices: string[];
};
